import { cn } from "@/shared/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

const badgeVariants = cva("inline-flex items-center justify-center rounded-full text-nowrap", {
    variants: {
        // This is a combination of the tractie badge component (default and error variant), and the tractie badge component (the other variants).
        variant: {
            default: "bg-brand-secondary text-content-default-inverse",
            error: "bg-error-default text-content-default-inverse",
            green: "bg-accent-green-default text-content-default-inverse",
            gray: "bg-accent-gray-default text-content-default-inverse",
            red: "bg-accent-red-default text-content-default-inverse",
            blue: "bg-accent-blue-default text-content-default-inverse",
            orange: "bg-accent-orange-default text-content-default-inverse",
            yellow: "bg-accent-yellow-default text-content-on-accent-yellow-default",
            purple: "bg-accent-purple-default text-content-default-inverse",
            pink: " bg-accent-pink-default text-content-default-inverse",
            "green-subtle": "bg-accent-green-subtle text-content-on-accent-subtle",
            "gray-subtle": "bg-accent-gray-subtle text-content-on-accent-subtle",
            "red-subtle": "bg-accent-red-subtle text-content-on-accent-subtle",
            "blue-subtle": "bg-accent-blue-subtle text-content-on-accent-subtle",
            "orange-subtle": "bg-accent-orange-subtle text-content-on-accent-subtle",
            "yellow-subtle": "bg-accent-yellow-subtle text-content-on-accent-subtle",
            "purple-subtle": "bg-accent-purple-subtle text-content-on-accent-subtle",
            "pink-subtle": "bg-accent-pink-subtle text-content-on-accent-subtle",
        },
        size: {
            s: "h-[1rem] aspect-square text-xs px-1",
            m: "py-0.5 px-2 text-xs",
            l: "py-1 px-3 text-s",
        },
    },
    defaultVariants: {
        variant: "gray-subtle",
        size: "m",
    },
});

export type BadgeVariant = VariantProps<typeof badgeVariants>["variant"];

export function Badge({
    className,
    variant,
    size,
    asChild = false,
    ...props
}: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
    const Comp = asChild ? Slot : "span";

    return (
        <Comp
            data-slot="badge"
            className={cn(
                badgeVariants({
                    variant,
                    size: size === undefined && (variant === "default" || variant === "error") ? "s" : size,
                }),
                className
            )}
            data-testid="badge"
            {...props}
        />
    );
}
